import exportFromJSON from "export-from-json";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import {
    createReviewAPI,
    deleteReviewAPI,
    getAllpublicReviewAPI,
    updateReviewAPI,
} from "../../../../api/publicReviewAPI.ts";
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

const ReviewList = () => {
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [newReview, setNewReview] = useState({ comment: "", images: [] });
    const [editReview, setEditReview] = useState({ comment: "", images: [] });

    const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);
    const toggleCreateModal = () => setIsCreateModalOpen(!isCreateModalOpen);
    const toggleEditModal = () => setIsEditModalOpen(!isEditModalOpen);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const response = await getAllpublicReviewAPI();
            setReviews(response.data.data?.reverse());
        } catch (error) {
            toast.error("Failed to fetch reviews", { position: "top-center" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const handleDelete = async (review) => {
        try {
            await deleteReviewAPI({ id: review._id });
            setReviews((prev) => prev.filter((r) => r._id !== review._id));
            toast.success("Review deleted successfully", { position: "top-center" });
            toggleDeleteModal();
        } catch (error) {
            toast.error(`Failed to delete review: ${error.message}`, { position: "top-center" });
        }
    };

    const handleCreate = async () => {
        try {
            const formData = new FormData();
            formData.append("comment", newReview.comment);
            newReview.images.forEach((image) => formData.append("images", image));

            const response = await createReviewAPI({ formData });
            setReviews((prev) => [response.data.data, ...prev]);
            setNewReview({ comment: "", images: [] });
            toast.success("Review created successfully", { position: "top-center" });
            toggleCreateModal();
        } catch (error) {
            toast.error(`Failed to create review: ${error.message}`, { position: "top-center" });
        }
    };

    const handleEdit = async () => {
        try {
            const formData = new FormData();
            formData.append("comment", editReview.comment);
            editReview.images.forEach((image) => formData.append("images", image));

            const response = await updateReviewAPI({ id: selectedReview._id, formData });
            setReviews((prev) =>
                prev.map((r) => (r._id === selectedReview._id ? response.data.data : r))
            );
            toast.success("Review updated successfully", { position: "top-center" });
            toggleEditModal();
        } catch (error) {
            toast.error(`Failed to update review: ${error.message}`, { position: "top-center" });
        }
    };

    const openEditModal = (review) => {
        setSelectedReview(review);
        setEditReview({ comment: review.comment, images: [] }); // Images are not preloaded; user must re-upload
        toggleEditModal();
    };

    const exportData = (type) => {
        const fileName = "reviews";
        const exportType = type === "csv" ? exportFromJSON.types.csv : exportFromJSON.types.xls;
        const exportData = reviews.map((review) => ({
            Comment: review.comment,
            CreatedAt: new Date(review.createdAt).toLocaleDateString(),
            HasImage: review.profile ? "Yes" : "No",
        }));
        exportFromJSON({ data: exportData, fileName, exportType });
    };

    // Dropzone for Create Modal
    const onDropCreate = useCallback((acceptedFiles) => {
        setNewReview((prev) => ({ ...prev, images: [...prev.images, ...acceptedFiles] }));
    }, []);

    const { getRootProps: getRootPropsCreate, getInputProps: getInputPropsCreate } = useDropzone({
        onDrop: onDropCreate,
        accept: "image/*",
        multiple: true,
    });

    // Dropzone for Edit Modal
    const onDropEdit = useCallback((acceptedFiles) => {
        setEditReview((prev) => ({ ...prev, images: [...prev.images, ...acceptedFiles] }));
    }, []);

    const { getRootProps: getRootPropsEdit, getInputProps: getInputPropsEdit } = useDropzone({
        onDrop: onDropEdit,
        accept: "image/*",
        multiple: true,
    });

    const columns = [
        {
            name: "Comment",
            selector: (row) => row.comment,
            cell: (row) => (
                <div
                    className="text-truncate"
                    style={{ maxWidth: "200px", cursor: "pointer" }}
                    onClick={() => openEditModal(row)}
                >
                    {row.comment}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Profile Image",
            selector: (row) => row.profile,
            cell: (row) => (
                <div className="d-flex align-items-center">
                    {row.profile ? (
                        <img
                            src={row.profile}
                            alt="Profile"
                            className="img-fluid rounded"
                            style={{ maxWidth: "50px", maxHeight: "50px", objectFit: "cover" }}
                            onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/50")}
                        />
                    ) : (
                        <span className="text-muted">No image</span>
                    )}
                </div>
            ),
        },
        {
            name: "Created At",
            selector: (row) => row.createdAt,
            cell: (row) => (
                <span className="text-muted">
                    {new Date(row.createdAt).toLocaleDateString()}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <Button
                        size="sm"
                        color="outline-primary"
                        onClick={() => openEditModal(row)}
                    >
                        <i className="bi bi-pencil-fill me-1"></i>
                        <span className="d-none d-md-inline">Edit</span>
                    </Button>
                    <Button
                        size="sm"
                        color="outline-danger"
                        onClick={() => {
                            setSelectedReview(row);
                            toggleDeleteModal();
                        }}
                    >
                        <i className="bi bi-trash-fill me-1"></i>
                        <span className="d-none d-md-inline">Delete</span>
                    </Button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    const expandableRowsComponent = ({ data }) => (
        <div className="p-2 bg-light">
            <Row>
                <Col xs="12">
                    <strong>Full Comment:</strong> {data.comment}
                </Col>
            </Row>
        </div>
    );

    return (
        <React.Fragment>
            <Head title="Review List" />
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Review List
                            </BlockTitle>
                            <BlockDes className="text-muted">
                                <p>You have a total of {reviews.length} reviews.</p>
                            </BlockDes>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <Row className="g-2">
                                <Col>
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={() => exportData("csv")}
                                    >
                                        <i className="bi bi-file-earmark-text me-1"></i>
                                        <span className="d-none d-md-inline">Export CSV</span>
                                    </Button>
                                </Col>
                                <Col>
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={() => exportData("xls")}
                                    >
                                        <i className="bi bi-file-earmark-excel me-1"></i>
                                        <span className="d-none d-md-inline">Export Excel</span>
                                    </Button>
                                </Col>
                                <Col>
                                    <Button color="success" onClick={toggleCreateModal}>
                                        <i className="bi bi-plus-lg me-1"></i>
                                        <span className="d-none d-md-inline">Add Review</span>
                                    </Button>
                                </Col>
                            </Row>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <div className="text-center">
                                    <div
                                        className="spinner-grow text-primary"
                                        style={{ width: "3rem", height: "3rem" }}
                                        role="status"
                                    >
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading reviews, please wait...</p>
                                </div>
                            </div>
                        ) : reviews.length === 0 ? (
                            <div className="text-center py-5">
                                <h5 className="text-muted">No reviews found</h5>
                                <Button color="primary" onClick={toggleCreateModal}>
                                    Add Your First Review
                                </Button>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={reviews}
                                columns={columns}
                                pagination
                                highlightOnHover
                                striped
                                dense
                                expandableRows
                                expandableRowsComponent={expandableRowsComponent}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                className="table-responsive"
                            />
                        )}
                    </PreviewCard>
                </BlockContent>
            </Content>

            {/* Create Review Modal */}
            <Modal isOpen={isCreateModalOpen} toggle={toggleCreateModal}>
                <ModalHeader toggle={toggleCreateModal}>Add New Review</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="comment">Comment</Label>
                            <Input
                                type="textarea"
                                name="comment"
                                id="comment"
                                value={newReview.comment}
                                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                                placeholder="Enter review comment"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Images</Label>
                            <div
                                {...getRootPropsCreate()}
                                className="dropzone border border-dashed p-4 text-center"
                                style={{ cursor: "pointer" }}
                            >
                                <input {...getInputPropsCreate()} />
                                <p className="text-muted mb-0">
                                    Drag & drop images here, or click to select files
                                </p>
                                <small className="text-muted">You can upload multiple images.</small>
                            </div>
                            {newReview.images.length > 0 && (
                                <div className="mt-2">
                                    <strong>Selected Files:</strong>
                                    <ul>
                                        {newReview.images.map((file, index) => (
                                            <li key={index}>{file.name}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleCreateModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleCreate}>
                        Create Review
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Edit Review Modal */}
            <Modal isOpen={isEditModalOpen} toggle={toggleEditModal}>
                <ModalHeader toggle={toggleEditModal}>Edit Review</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="editComment">Comment</Label>
                            <Input
                                type="textarea"
                                name="editComment"
                                id="editComment"
                                value={editReview.comment}
                                onChange={(e) => setEditReview({ ...editReview, comment: e.target.value })}
                                placeholder="Enter review comment"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Images</Label>
                            <div
                                {...getRootPropsEdit()}
                                className="dropzone border border-dashed p-4 text-center"
                                style={{ cursor: "pointer" }}
                            >
                                <input {...getInputPropsEdit()} />
                                <p className="text-muted mb-0">
                                    Drag & drop images here, or click to select files
                                </p>
                                <small className="text-muted">
                                    Upload new images to replace existing ones (optional).
                                </small>
                            </div>
                            {editReview.images.length > 0 && (
                                <div className="mt-2">
                                    <strong>Selected Files:</strong>
                                    <ul>
                                        {editReview.images.map((file, index) => (
                                            <li key={index}>{file.name}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleEditModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleEdit}>
                        Save Changes
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                onConfirm={handleDelete}
                item={selectedReview}
                itemName={selectedReview?.comment?.substring(0, 20) + "..."}
            />
        </React.Fragment>
    );
};

export default ReviewList;